"use client"

import React from 'react'
import { UIButton, type UIButtonProps } from './ui-button'
import {
  UIDropdownMenu,
  UIDropdownMenuContent,
  UIDropdownMenuItem,
  UIDropdownMenuTrigger,
} from '../forms/ui-dropdown-menu'
import { ChevronDown } from 'lucide-react'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'

export interface UIButtonSplitProps extends Omit<UIButtonProps, 'onClick'> {
  onMainAction?: () => void
  items?: Array<{
    label: string
    onClick: () => void
    disabled?: boolean
  }>
}

/**
 * Split Button Component
 * Based on common split button patterns for primary action + options
 * Use when there's a primary action with additional related actions
 *
 * Note: The ref is forwarded to the main action button only.
 * The dropdown trigger button does not support ref forwarding due to
 * compound component limitations — it is managed internally by UIDropdownMenuTrigger.
 */
export const UIButtonSplit = React.forwardRef<HTMLButtonElement, UIButtonSplitProps>(
  ({ onMainAction, items = [], children, className, variant = "default", size = "default", ...props }, ref) => {
    return (
      <UIBox className="flex">
        <UIButton
          ref={ref}
          variant={variant}
          size={size}
          className={cn(
            "rounded-r-none border-r-0",
            className
          )}
          onClick={onMainAction}
          {...props}
        >
          {children}
        </UIButton>

        <UIDropdownMenu>
          <UIDropdownMenuTrigger asChild>
            <UIButton
              variant={variant}
              size={size}
              aria-label="More options"
              className={cn(
                "rounded-l-none px-2",
                size === "sm" && "px-1.5",
                size === "lg" && "px-3"
              )}
            >
              <ChevronDown className="h-4 w-4" />
            </UIButton>
          </UIDropdownMenuTrigger>
          <UIDropdownMenuContent align="end">
            {items.map((item, index) => (
              <UIDropdownMenuItem
                key={index}
                onClick={item.onClick}
                disabled={item.disabled}
              >
                {item.label}
              </UIDropdownMenuItem>
            ))}
          </UIDropdownMenuContent>
        </UIDropdownMenu>
      </UIBox>
    )
  }
)

UIButtonSplit.displayName = "UIButtonSplit"